import { Head, Link, router } from "@inertiajs/react"
import { Ban } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Contact {
  id: number
  display_name: string
  first_name: string | null
  last_name: string | null
  department: string | null
  phone: string | null
  email: string | null
  status: number
  marketing_events_count: number
}

interface Props {
  customer: Customer
  contacts: Contact[]
}

export default function Index({ customer, contacts }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Contacts", href: `/customers/${customer.id}/contacts` },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this contact?")) {
      router.delete(`/customers/${customer.id}/contacts/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Contacts - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Contacts for {customer.display_name}
          </h1>
          <Link
            href={`/customers/${customer.id}/contacts/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Contact
          </Link>
        </div>

        {contacts.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Department
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Phone
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Email
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/contacts/${contact.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {contact.display_name}
                    </Link>
                    {contact.status === 90 && (
                      <Ban className="text-hpi-red ml-1.5 inline-block h-4 w-4" />
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {contact.department}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {contact.phone}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {contact.email}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/contacts/${contact.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      Edit
                    </Link>
                    {contact.marketing_events_count > 0 && (
                      <Link
                        href={`/customers/${customer.id}/marketing_events?contact_id=${contact.id}`}
                        className="text-hpi-active-blue mr-2 hover:underline"
                      >
                        Marketing ({contact.marketing_events_count})
                      </Link>
                    )}
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No contacts found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Customer
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
